import FormSelect from 'components/Select/FormSelect';
import Text from 'components/Text/Text';
import { ArchivedLabel } from 'components/Select/ArchivedLabel';
import { TCreateTeaserSelectedItem } from 'modules/Teasers/CreateTeaser/createTeaserState/createTeaserContext';
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { IItemForGetShortInfoWithStatus, EntityStatus } from 'types';
import { SHOW_ARCHIVED_RESTAURANTS } from 'appConstants';
import { Checkbox } from 'antd';
import { Rule } from 'antd/lib/form';
import { useTranslation } from 'react-i18next';
import SearchField from 'components/SearchField/SearchField';
import style from './MultipleSelect.module.scss';

interface IMultipleSelect {
	data: IItemForGetShortInfoWithStatus[];
	isLoading: boolean;
	refetch: () => void;
	responseError: boolean;
	selectedRestaurants: string[];
	selectedCustomer: TCreateTeaserSelectedItem;
	setRestaurants: (value: string[]) => void;
}

const MultipleSelect: React.FC<IMultipleSelect> = ({
	data,
	isLoading,
	refetch,
	responseError,
	selectedRestaurants,
	selectedCustomer,
	setRestaurants,
}) => {
	const [searchString, setSearchString] = useState<string>('');
	const [showArchived, setShowArchived] = useState<boolean>(
		localStorage.getItem(SHOW_ARCHIVED_RESTAURANTS) === 'true',
	);
	const { t } = useTranslation('error');

	const validationRules = useMemo<Rule[]>(() => [{ required: true, message: t('selectOneRestaurant') }], [t]);

	const visibleData = useMemo(() => {
		const items = showArchived
			? data
			: data.filter((el) => el.status === EntityStatus.Active || selectedRestaurants.includes(el.id));

		return items.map((el) => ({
			...el,
			additionalContent: el.status === EntityStatus.Archived ? <ArchivedLabel /> : undefined,
		}));
	}, [data, showArchived, selectedRestaurants]);

	const filteredData = useMemo(() => {
		return searchString
			? visibleData.filter((el) => el.name.toLowerCase().includes(searchString.toLowerCase()))
			: visibleData;
	}, [searchString, visibleData]);

	const handleSelectAllData = useCallback(() => {
		if (visibleData.length === selectedRestaurants.length) {
			setRestaurants([]);
		} else {
			const IDsOfAllRestaurants = visibleData.map((el) => el.id);
			setRestaurants(IDsOfAllRestaurants);
		}
	}, [visibleData, selectedRestaurants, setRestaurants]);

	const toggleShowArchived = useCallback(() => {
		localStorage.setItem(SHOW_ARCHIVED_RESTAURANTS, JSON.stringify(!showArchived));
		setShowArchived((prevValue) => !prevValue);
	}, [showArchived]);

	useEffect(() => {
		setSearchString('');
	}, [selectedCustomer]);

	const renderSelectMenu = useCallback(
		(menu: React.ReactElement) => {
			const isChecked = () => {
				return visibleData.every((el) => selectedRestaurants.includes(el.id));
			};

			return (
				<>
					<div className={style.selectMenuHeader}>
						<Checkbox
							className={style.checkBox}
							checked={!!(visibleData.length && isChecked())}
							disabled={!visibleData.length}
							onChange={handleSelectAllData}>
							<Text
								translationNameSpace="form"
								translationKey="selectAllRestaurantsLabel"
								asFragment={true}
							/>
						</Checkbox>
						<Checkbox className={style.checkBox} checked={showArchived} onChange={toggleShowArchived}>
							<Text translationNameSpace="modals" translationKey="showArchived" asFragment={true} />
						</Checkbox>
						<SearchField
							onChangeSelectSearchString={setSearchString}
							searchString={searchString}
							className={style.searchField}
						/>
					</div>
					{menu}
				</>
			);
		},
		[
			visibleData,
			selectedRestaurants,
			handleSelectAllData,
			setSearchString,
			searchString,
			showArchived,
			toggleShowArchived,
		],
	);

	return (
		<FormSelect
			name="selectedRestaurants"
			label="restaurantsLabel"
			options={filteredData}
			fetchError={responseError}
			loading={isLoading}
			onRefetch={refetch}
			onChange={setRestaurants}
			disabled={!selectedCustomer || isLoading}
			dropdownRender={renderSelectMenu}
			mode="multiple"
			showSearch={false}
			counterValue={selectedRestaurants.length}
			validationRules={validationRules}
		/>
	);
};

export default React.memo(MultipleSelect);
